import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { Spacings, mediaQuery } from '@styles/index';
import { useRouter } from 'next/router';
import CourseCarouselSkeleton from '@skeletons/CourseCard/CourseCarousel';
import NewBatchCard from '@cont/EnrolledCourse/NewBatchCard';
import { getLink as getBatchLink } from '@models/Batch';
import segmentStorage from '@events/segmentStorage';
import { EDUCATOR_BATCHES } from '@constants/segment';
import Typography from 'aquilla/core/atoms/Typography';
import BottomChevron from 'aquilla/icons/actions/BottomChevron';
import EducatorEmptyCourse from './EducatorEmptyCourse';
import useInfiniteFetchNew from './hooks/useInfiniteFetchNew';

const Container = styled.div`
  margin-bottom: -${Spacings.SPACING_10B};
  border-bottom: 1px solid var(--color-divider);
`;
const InnerContainer = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_10B} 0 ${Spacings.SPACING_10B} 0;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B} 0;
  }
`;
const StyledBatchCard = styled(NewBatchCard)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: unset;

    > div:nth-child(2) {
      height: unset;
      > img {
        height: auto;
        max-width: unset;
      }
    }
  }
`;
const StyledViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B} ${Spacings.SPACING_2B}
    ${Spacings.SPACING_4B};
`;
const StyledViewMoreButton = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_1B}
    ${Spacings.SPACING_2B};
`;
const StyledChevronWrapper = styled.span`
  margin-top: 5px;
`;

const useBatchesByFilters = (
  { userName },
  { filtersApplied },
  options = { fallbackData: undefined }
) => {
  const filterParams = {
    username: userName.toLowerCase(),
    ...filtersApplied
  };
  const key = 'sheldon/v1/list/batch';
  const infiniteResponse = useInfiniteFetchNew(
    key,
    { applyOffsetsAutomatically: false, params: filterParams },
    {
      fallbackData: options.fallbackData,
      revalidateOnMount: options.revalidateOnMount
    }
  );
  return {
    key,
    ...infiniteResponse
  };
};

const Loader = () => <CourseCarouselSkeleton number={4} />;

const BatchesList = ({ authorID, filtersApplied }) => {
  const router = useRouter();

  const {
    data: filteredBatchesResponse,
    setSize,
    isValidating: isFetchingFilteredBatches
  } = useBatchesByFilters(
    { userName: authorID?.slice(1) },
    { filtersApplied },
    { revalidateOnMount: false }
  );

  const handleViewMore = () => {
    setSize((size) => size + 1);
  };

  const renderBatches = useCallback(
    (batchInfo) => {
      const handleClick = () => {
        segmentStorage.setItem('lps', EDUCATOR_BATCHES);
        router.push(getBatchLink(batchInfo));
      };
      return (
        <StyledBatchCard as="div" batch={batchInfo} onClick={handleClick} />
      );
    },
    [router]
  );
  return (
    <Container>
      <InnerContainer>
        {filteredBatchesResponse?.results?.map((batch) => {
          return renderBatches(batch);
        })}
        {!isFetchingFilteredBatches &&
          filteredBatchesResponse?.results?.length === 0 && (
            <EducatorEmptyCourse
              header="No batches found"
              detail="The educator doesn’t have any batches. Select a different category or try with different filters. "
            />
          )}
        {!isFetchingFilteredBatches &&
        filteredBatchesResponse?.results?.length <
          filteredBatchesResponse?.count ? (
          <StyledViewMoreButtonContainer onClick={handleViewMore}>
            <StyledViewMoreButton>
              <Typography variant="h6">View more</Typography>{' '}
              <StyledChevronWrapper>
                <BottomChevron height={16} width={16} />
              </StyledChevronWrapper>
            </StyledViewMoreButton>
          </StyledViewMoreButtonContainer>
        ) : (
          <></>
        )}
        {isFetchingFilteredBatches ? <Loader /> : <></>}
      </InnerContainer>
    </Container>
  );
};

export default BatchesList;
